import { EventType } from 'features/dashboard/types/camera-event'
import { Card } from 'primereact/card'
import { ScrollPanel } from 'primereact/scrollpanel'
import { useMemo, useState } from 'react'
import { AhcrEventCard } from './dashboard-notification-ahcr-item'
import { AnprEventCard } from './dashboard-notification-anpr-item'

import DashboardNotificationDialog from '../dashboard-notification-dialog/dashboard-notification-item-detail-dialog'
import './dashboard-notification-grid.scss'
import { CameraStatusCard } from './dashboard-notificaiton-status-item'

interface CameraEvent {
  cameraId: string
  eventType: string
  timestamp: string
  imageBase64: string
  metadata: Record<string, string>
}

export interface NotificationsProps {
  eventHistory: CameraEvent[]
}

const NotificationGrid = (props: NotificationsProps) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const [selectedEvent, setSelectedEvent] = useState<CameraEvent | null>(null)
  const [showImageDialog, setShowImageDialog] = useState(false)
  const [displayMode, setDisplayMode] = useState<'detailed' | 'simple'>(
    'detailed'
  )

  const toggleDisplayMode = () => {
    setDisplayMode((prev) => (prev === 'detailed' ? 'simple' : 'detailed'))
  }

  const handleImageClick = (event: CameraEvent) => {
    setSelectedEvent(event)
    setSelectedImage(event.imageBase64)
    setShowImageDialog(true)
  }

  const onHideNotificationDialog = () => {
    setShowImageDialog(false)
  }

  const todayEvents = useMemo(() => {
    return props.eventHistory.filter((event) => {
      const eventDate = new Date(event.timestamp)
      const today = new Date()
      return eventDate.toDateString() === today.toDateString()
    })
  }, [props.eventHistory])

  const headerTemplate = (
    <div className='notifications-header-content'>
      <div className='header-left'>
        <div className='notification-icon'>
          <i className='pi pi-bell'></i>
        </div>
        <div className='header-text'>
          <h3 className='header-title'>Notificações em Tempo Real</h3>
        </div>
      </div>
      <div className='header-right'>
        <button
          className='display-toggle-btn'
          onClick={toggleDisplayMode}
          title={
            displayMode === 'detailed'
              ? 'Visualização Simples'
              : 'Visualização Detalhada'
          }
        >
          <i
            className={`pi ${displayMode === 'detailed' ? 'pi-list' : 'pi-th-large'
              }`}
          ></i>
        </button>
      </div>
    </div>
  )

  return (
    <>
      <Card
        className='notifications-card stats-main-card'
        header={headerTemplate}
      >
        <div>
          {props.eventHistory.length > 0 ? (
            <ScrollPanel className='notifications-scrollpanel'>
              {todayEvents.length > 0 && (
                <div
                  className={`notifications-grid ${displayMode === 'simple' ? 'simple-grid' : ''
                    }`}
                >
                  {todayEvents.map((event, index) => {
                    // Check if this is a camera status event
                    if (
                      event.eventType === EventType.CameraStarting ||
                      event.eventType === EventType.CameraStopped ||
                      event.eventType === EventType.CameraRestarting
                    ) {
                      return (
                        <CameraStatusCard
                          key={`status-${index}`}
                          event={event}
                          index={index}
                        />
                      )
                    }
                    // Check if this is an AHCR (people) detection event
                    if (
                      event.eventType === EventType.AHCRDetection ||
                      event.metadata?.VehicleType === 'people'
                    ) {
                      return (
                        <AhcrEventCard
                          key={`ahcr-${index}`}
                          event={event}
                          index={index}
                          displayMode={displayMode}
                          onImageClick={handleImageClick}
                        />
                      )
                    }
                    // Default to ANPR event body for vehicle detections
                    return (
                      <AnprEventCard
                        key={`anpr-${index}`}
                        event={event}
                        index={index}
                        displayMode={displayMode}
                        onImageClick={handleImageClick}
                      />
                    )
                  })}
                </div>
              )}
            </ScrollPanel>
          ) : (
            <div className='empty-state'>
              <div className='empty-icon'>
                <i className='pi pi-bell-slash'></i>
              </div>
              <h4 className='empty-title'>Nenhuma Detecção Registrada</h4>
              <p className='empty-subtitle'>
                Aguardando capturas das câmeras de segurança...
              </p>
              <div className='pulse-indicator'>
                <div className='pulse-dot'></div>
                <span>Monitorando em tempo real</span>
              </div>
            </div>
          )}
        </div>
      </Card>

      <DashboardNotificationDialog
        onHide={onHideNotificationDialog}
        selectedEvent={selectedEvent}
        selectedImage={selectedImage}
        visible={showImageDialog}
      />
    </>
  )
}

export default NotificationGrid
